import { ReactNode, useMemo, useState } from 'react';
import { Modal, ModalOverlay, useMultiStyleConfig } from '@chakra-ui/react';
import { isEqual } from '../../utils/utils';
import Box, { BoxProps } from '../Box/Box';
import Button from '../Button/Button';
import Divider from '../Divider/Divider';
import Icon from '../Icon/Icon';
import { FilterContext } from './Filter.context';
import { FilterStyle } from './Filter.theme';
import {
  FilterCategoryProps,
  FilterContextType,
  FilterData,
  FilterState,
  FilterType,
  FilterValue,
} from './Filter.types';
import { getDependents } from './Filter.utils';
import FilterAdd from './FilterAdd/FilterAdd';
import FilterItem from './FilterItem/FilterItem';
import FilterSearch from './FilterSearch/FilterSearch';
import FilterDate from './FilterDate/FilterDate';
import FilterSwitchAdapter from './FilterSwitchAdapter/FilterSwitchAdapter';

export interface FilterProps extends Omit<BoxProps, 'onChange'> {
  data: FilterData;
  defaultState?: FilterState;
  filtersDependOn?: string[];
  isLoading?: boolean;
  onChange: (state: FilterState) => void;
  searchTooltip?: ReactNode;
  showAdd?: boolean;
  showFilterIcon?: boolean;
  showSearch?: boolean;
  state: FilterState;
}

const Filter = (props: FilterProps) => {
  const {
    data,
    defaultState,
    filtersDependOn,
    isLoading,
    onChange,
    searchTooltip,
    showAdd = true,
    showFilterIcon = true,
    showSearch,
    state,
    ...rest
  } = props;

  const filterStyle = useMultiStyleConfig('Filter') as FilterStyle;

  const cleanState: FilterState = {};
  Object.entries(state).forEach(([category, values]) => {
    if (values?.length) {
      const cleanValues = values.filter((v) => v !== null && v !== '' && v !== undefined);
      if (cleanValues.length) {
        const isMultiple = !!data[category]?.isMultiple;
        cleanState[category] = isMultiple ? cleanValues : cleanValues.slice(0, 1);
      }
    }
  });

  const stateToRestore = useMemo<FilterState>(() => defaultState || cleanState, [defaultState, cleanState]);

  const [isPopoverOpen, setPopoverOpen] = useState<boolean>(false);

  const deleteFromState = (category: string, stateProp: FilterState): FilterState => {
    const filteredState = { ...stateProp };
    delete filteredState[category];

    const dependents = getDependents(data, category, filtersDependOn);

    dependents.forEach((dependent) => {
      delete filteredState[dependent];
    });

    return filteredState;
  };

  const onFilterChange = (category: string, value: FilterValue) => {
    let newState = { ...cleanState };
    if (value && value.length > 0) {
      newState[category] = value;
      const dependents = getDependents(data, category, filtersDependOn);
      if (dependents.length) {
        dependents.forEach((key) => {
          newState = deleteFromState(key, newState);
        });
      }
    } else if (newState[category]) {
      newState = deleteFromState(category, newState);
    }
    onChange(newState);
  };

  const onFilterClear = (category: string) => {
    onChange(deleteFromState(category, cleanState));
  };

  const onClearFilters = () => {
    onChange(stateToRestore || {});
  };

  const filters = {
    dateRange: {},
    search: {},
    select: {},
    switch: {},
    tag: {},
  } as Record<FilterType, Record<string, FilterCategoryProps>>;

  Object.entries(data).forEach(([category, value]) => {
    filters[value.type || 'tag'][category] = value;
  });

  const showClearFilters = !isEqual(stateToRestore, state);

  const contextValue: FilterContextType = useMemo(
    () => ({
      data,
      filtersDependOn,
      isLoading,
      onFilterChange,
      onFilterClear,
      setPopoverOpen,
      state: cleanState,
    }),
    [data, filtersDependOn, isLoading, onFilterChange, onFilterClear, setPopoverOpen, cleanState],
  );

  return (
    <FilterContext value={contextValue}>
      <Box sx={filterStyle.container} {...rest}>
        <Modal isOpen={isPopoverOpen} onClose={() => {}}>
          {isPopoverOpen && <ModalOverlay />}
        </Modal>
        <Box sx={filterStyle.content}>
          {showFilterIcon && <Icon name="Filter" sx={filterStyle.icon} />}

          {Object.keys(filters.switch).map((category) => (
            <FilterSwitchAdapter key={category} category={category} />
          ))}

          {Object.keys(filters.dateRange).map((category) => (
            <FilterDate key={category} category={category} />
          ))}

          {Object.keys(filters.select).map((category) => (
            <FilterItem key={category} category={category} />
          ))}
          {Object.keys(filters.tag).map((category) => {
            if (!cleanState[category]) {
              return;
            }
            return <FilterItem key={category} category={category} />;
          })}

          {showAdd && <FilterAdd onChange={onFilterChange} />}
        </Box>
        {(showClearFilters || showSearch) && (
          <Box sx={filterStyle.rightContent}>
            {showClearFilters && (
              <Button
                isDisabled={isLoading}
                leftIconName="Cross"
                minWidth="7.5rem"
                onClick={onClearFilters}
                size="sm"
                variant="tertiary"
              >
                Clear filters
              </Button>
            )}
            {showSearch && (
              <>
                <Divider flexShrink="0" orientation="vertical" size="1" variant="solid" />
                <FilterSearch
                  onChange={onFilterChange}
                  value={cleanState.search?.length ? cleanState.search[0] : ''}
                  searchTooltip={searchTooltip}
                />
              </>
            )}
          </Box>
        )}
      </Box>
    </FilterContext>
  );
};

export default Filter;
